import { useState, useRef, useEffect, useMemo } from 'react'
import { Grid } from '@/components/shared/Grid'
import { SheetClose } from '@/components/ui/sheet'
import { useTranslation } from 'react-i18next'
import type { PortfolioItem } from '@/types/models'
import { useFlashDetail } from '@/hooks/useFlashDetail'

interface ArtistContentSelectorProps {
  artistId: string
  onSelect: (items: PortfolioItem[]) => void
  selectedItems: PortfolioItem[]
  maxSelection?: number
  referenceExplanationText?: string
  /** Deep-link flash to open pre-selected (e.g. from chat booking flow). */
  initialFlashId?: string | null
}

export function ArtistContentSelector({
  artistId,
  onSelect,
  selectedItems,
  maxSelection = 4,
  referenceExplanationText,
  initialFlashId,
}: ArtistContentSelectorProps) {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<'flashes' | 'tattoos'>('flashes')
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const { flash } = useFlashDetail(initialFlashId || undefined)
  const initialFlashProcessedReference = useRef(false)

  useEffect(() => {
    initialFlashProcessedReference.current = false
  }, [initialFlashId])

  useEffect(() => {
    if (initialFlashId && scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = 0
    }
  }, [initialFlashId, artistId])

  useEffect(() => {
    if (initialFlashId && flash && !initialFlashProcessedReference.current) {
      initialFlashProcessedReference.current = true
      if (!selectedItems.some(i => i.id === flash.id)) {
        onSelect([...selectedItems, flash])
      }
    }
  }, [initialFlashId, flash, selectedItems, onSelect])

  const handleItemToggle = (item: PortfolioItem) => {
    const isSelected = selectedItems.some(i => i.id === item.id)

    if (isSelected) {
      onSelect(selectedItems.filter(i => i.id !== item.id))
    } else {
      if (selectedItems.length < maxSelection) {
        onSelect([...selectedItems, item])
      }
    }
  }

  const selectedItemIds = selectedItems.map(i => i.id)

  const injectFlashIfMissing = useMemo(() => {
    if (!initialFlashId || !flash || flash.id !== initialFlashId) return undefined
    return [flash]
  }, [initialFlashId, flash])

  return (
    <div className="flex max-h-[85vh] flex-col">
      <SheetClose asChild>
        <button
          type="button"
          className="flex flex-shrink-0 cursor-pointer items-center justify-center pb-1.5 pt-2.5 touch-manipulation text-muted-foreground/45 transition-colors hover:text-muted-foreground/75 active:text-muted-foreground"
          aria-label={t('common.close', 'Close')}
        >
          <svg
            className="pointer-events-none w-10 shrink-0 text-muted-foreground/40"
            viewBox="0 0 40 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <path
              d="M 2.5 2.5 L 20 7 L 37.5 2.5"
              stroke="currentColor"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </SheetClose>

      {referenceExplanationText && (
        <p className="px-4 pb-3 pt-1 text-center text-xs leading-relaxed text-muted-foreground">
          {referenceExplanationText}
        </p>
      )}

      <div className="flex flex-shrink-0 items-center justify-center pb-3">
        <div className="flex items-center gap-8">
          <button
            type="button"
            onClick={() => setActiveTab('flashes')}
            className={`pb-2 text-base font-medium transition-colors ${
              activeTab === 'flashes'
                ? 'border-b-2 border-primary text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('navigation.flashes')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('tattoos')}
            className={`pb-2 text-base font-medium transition-colors ${
              activeTab === 'tattoos'
                ? 'border-b-2 border-primary text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('navigation.tattoos')}
          </button>
        </div>
      </div>

      <div
        ref={scrollAreaRef}
        className="min-h-0 flex-1 overflow-y-auto rounded-lg px-2 pb-4"
        style={{ height: '400px' }}
      >
        <div style={{ display: activeTab === 'flashes' ? 'block' : 'none' }}>
          <Grid
            selectedCollections={['flashes']}
            selectedStyle={t('onskin.filters.allStyles')}
            selectedArtist={artistId}
            selectedLocations={[t('onskin.filters.allLocations')]}
            sorting="recent"
            isProfilePage={true}
            selectionMode={true}
            selectedItems={selectedItemIds}
            onItemToggle={handleItemToggle}
            hideArtistInfo={true}
            maxSelection={maxSelection}
            isNestedContext={true}
            leadWithItemId={initialFlashId ?? undefined}
            prefetchUntilItemId={initialFlashId ?? undefined}
            scrollRootRef={scrollAreaRef}
            injectItemsIfMissing={injectFlashIfMissing}
          />
        </div>
        <div style={{ display: activeTab === 'tattoos' ? 'block' : 'none' }}>
          <Grid
            selectedCollections={['portfolioItems']}
            selectedStyle={t('onskin.filters.allStyles')}
            selectedArtist={artistId}
            selectedLocations={[t('onskin.filters.allLocations')]}
            sorting="recent"
            isProfilePage={true}
            selectionMode={true}
            selectedItems={selectedItemIds}
            onItemToggle={handleItemToggle}
            hideArtistInfo={true}
            maxSelection={maxSelection}
            isNestedContext={true}
            scrollRootRef={scrollAreaRef}
          />
        </div>
      </div>
    </div>
  )
}
